import { useHistory, useLocation, useParams } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { Badge, Card, CardBody, CardHeader, Col, Container, Label, Row, Spinner } from "reactstrap";
import { useCallback, useMemo, useState } from "react";
import Navigator from "src/components/Navigator";
import { EMPTY_LIST, INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ComplianceScreeningQueriesService from "src/modules/3rd-party-management/apis/admin/compliance/screening/QueriesService";
import ComplianceMonitoringQueriesService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/QueriesService";
import { ComplianceQueryChoices, ComplianceQueryItemTypes, ComplianceQueryTypes } from "src/modules/3rd-party-management/constants";
import DateUtils from "src/services/utils/DateUtils";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import FilteringComponent from "./components/FilteringComponent";

const DetailHeader = ({ t, id, type, queryData, isFetching }) => {
    if (isFetching) {
        return (
            <Card>
                <CardBody>
                    <Row className="mb-5">
                        <Col sm="12" md="3">
                            <div className="dt-field dt-skeleton mb-1" style={{ width: '40%' }}></div>
                            <div className="dt-field dt-skeleton" style={{ width: '70%' }}></div>
                        </Col>
                        <Col sm="12" md="3">
                            <div className="dt-field dt-skeleton mb-1" style={{ width: '40%' }}></div>
                            <div className="dt-field dt-skeleton" style={{ width: '70%' }}></div>
                        </Col>
                        <Col sm="12" md="3">
                            <div className="dt-field dt-skeleton mb-1" style={{ width: '40%' }}></div>
                            <div className="dt-field dt-skeleton" style={{ width: '70%' }}></div>
                        </Col>
                        <Col sm="12" md="3">
                            <div className="dt-field dt-skeleton mb-1" style={{ width: '40%' }}></div>
                            <div className="dt-field dt-skeleton" style={{ width: '70%' }}></div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        )
    }

    const dateUtils = new DateUtils();

    return (
        <Card>
            <CardBody>
                <Row>
                    <Col sm="12" md="6" lg="3" className="mb-4">
                        <div className="d-flex">
                            <div className="avatar-sm me-3">
                                <span className="avatar-title bg-light rounded-circle text-primary font-size-24">
                                    <i className="ri-filter-3-line"></i>
                                </span>
                            </div>
                            <div className="flex-1 align-self-center overflow-hidden">
                                <Label className="form-label">
                                    {t('Process Type')}:
                                </Label>
                                <p className="text-muted mb-0">
                                    {t(type.label)}
                                </p>
                            </div>
                        </div>
                    </Col>

                    <Col sm="12" md="6" lg="3" className="mb-4">
                        <div className="d-flex">
                            <div className="avatar-sm me-3">
                                <span className="avatar-title bg-light rounded-circle text-primary font-size-24">
                                    <i className="ri-text"></i>
                                </span>
                            </div>
                            <div className="flex-1 align-self-center overflow-hidden">
                                <Label className="form-label">
                                    {t('Title')}:
                                </Label>
                                <p className="text-muted mb-0">
                                    {queryData?.name}
                                </p>
                            </div>
                        </div>
                    </Col>

                    <Col sm="12" md="6" lg="3" className="mb-4">
                        <div className="d-flex">
                            <div className="avatar-sm me-3">
                                <span className="avatar-title bg-light rounded-circle text-primary font-size-24">
                                    <i className="ri-file-text-line"></i>
                                </span>
                            </div>
                            <div className="flex-1 align-self-center overflow-hidden">
                                <Label className="form-label">
                                    {t('Description')}:
                                </Label>
                                <p className="text-muted mb-0">
                                    {queryData?.description}
                                </p>
                            </div>
                        </div>
                    </Col>

                    <Col sm="12" md="6" lg="3" className="mb-4">
                        <div className="d-flex">
                            <div className="avatar-sm me-3">
                                <span className="avatar-title bg-light rounded-circle text-primary font-size-24">
                                    <i className="ri-time-line"></i>
                                </span>
                            </div>
                            <div className="flex-1 align-self-center overflow-hidden">
                                <Label className="form-label">
                                    {t('Created')}:
                                </Label>
                                <p className="text-muted mb-0">
                                    {queryData?.createdAt && (
                                        <>
                                            <span className="me-1">
                                                {dateUtils.convertDateToDate(
                                                    queryData.createdAt,
                                                    INTERNATIONAL_DATE_FORMAT
                                                )}
                                            </span>
                                            <span className="me-1">
                                                {t('by')}
                                            </span>
                                            <Badge color="info" className="bg-info">
                                                {queryData.createdBy?.fullName}
                                            </Badge>
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                    </Col>

                    <Col sm="12" md="6" lg="12" className="mb-4">
                        <div className="d-flex">
                            <div className="avatar-sm me-3">
                                <span className="avatar-title bg-light rounded-circle text-primary font-size-24">
                                    <i className="ri-group-line"></i>
                                </span>
                            </div>
                            <div className="flex-1 align-self-center overflow-hidden">
                                <Label className="form-label">
                                    {t('Entity Groups')}:
                                </Label>
                                {queryData?.choices?.map((choice, choiceIndex) => {
                                    const choiceTitle = ComplianceQueryChoices.find((ch) => ch.value === choice);

                                    return (
                                        <p className="text-muted mb-0" key={choiceIndex}>
                                            <i className="ri-check-line me-1 font-size-15 text-info align-middle" />
                                            {t(choiceTitle?.label || choice)}
                                        </p>
                                    )
                                })}
                            </div>
                        </div>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    )
}

const ComplianceQueryDetailsBody = ({ t, type, id }) => {
    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        enabledItems: true
    });

    const history = useHistory();

    const dateUtils = new DateUtils();

    const queriesService = type.value === 'screening' ?
        ComplianceScreeningQueriesService.getInstance() :
        ComplianceMonitoringQueriesService.getInstance();

    const queryDetailQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-compliance-query-details',
            type.value,
            id
        ],
        queryFn: async () => {
            return await queriesService.fetchQueryDetail(id);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('Failed to load query details. Please try again.'), {
                type: 'error'
            });
        }
    });

    const {
        data: resultData,
        isFetching: itemsFetching,
        isFetched: itemsFetched
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-compliance-query-items',
            id,
            filters
        ],
        queryFn: async () => {
            return await queriesService.fetchQueryItems(id, filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('Failed to load search items. Please try again.'), {
                type: 'error'
            });
        }
    });

    const tableColumns = [
        {
            dataField: "secondaryId",
            text: t("Number"),
            sort: false
        },
        {
            dataField: "supplier",
            text: t("Search Item"),
            sort: false,
            style: {
                width: '200px'
            },
            formatter: (_cell, { choiceKey, itemType, ...elseProps }) => {
                switch (itemType) {
                    case 'organization_legal':
                        return elseProps.organizationLegal?.legalEntity?.title || '--';
                    case 'share_holder':
                        return (choiceKey === 'shareholder_company' ? elseProps.shareHolder?.companyName : elseProps.shareHolder?.fullName) || '--';
                    case 'kyb_management_member':
                        return elseProps.kybManagementTeamMember?.fullName || '--';
                    case 'team_manager':
                        return elseProps.teamManager?.fullName || '--';
                    case 'organization':
                        return elseProps.organization?.name || '--';
                    default:
                        return '--';
                }
            }
        },
        {
            dataField: "supplier.name",
            text: t("Third Party"),
            sort: false
        },
        {
            dataField: "type",
            text: t("Type"),
            sort: false,
            formatter: (cell) => {
                const queryType = ComplianceQueryItemTypes.find((itemType) => itemType.value === cell);

                return t(queryType?.label || cell) || '--';
            }
        },
        {
            dataField: "organization.registrationNumber",
            text: t("Registration Number"),
            sort: false,
            formatter: (cell) => {
                return cell || '--';
            }
        },
        {
            dataField: "itemType",
            text: t("Relation"),
            sort: false,
            formatter: (cell, { choiceKey }) => {
                const choice = ComplianceQueryChoices.find((choice) => choice.value === choiceKey);

                const relation = choice?.relations?.find((relation) => relation.value === cell);

                return `${t(relation?.label || cell)}`;
            }
        },
        {
            dataField: "choiceKey",
            text: t("Date of Birth"),
            sort: false,
            formatter: (cell, { itemType, ...elseProps }) => {
                if (cell === 'shareholder_individuals') {
                    if (elseProps.shareHolder?.datesOfBirth) {
                        return dateUtils.convertDateToDate(
                            elseProps.shareHolder.datesOfBirth,
                            INTERNATIONAL_DATE_FORMAT
                        );
                    }
                }

                if (cell === 'ubo') {
                    if (elseProps.shareHolder?.datesOfBirth) {
                        return dateUtils.convertDateToDate(
                            elseProps.shareHolder.datesOfBirth,
                            INTERNATIONAL_DATE_FORMAT
                        );
                    }
                }

                if (cell === 'management') {
                    if (itemType === 'kyb_management_member') {
                        if (elseProps.kybManagementTeamMember?.dateOfBirth) {
                            return dateUtils.convertDateToDate(
                                elseProps.kybManagementTeamMember.dateOfBirth,
                                INTERNATIONAL_DATE_FORMAT
                            );
                        }
                    }

                    if (itemType === 'team_manager') {
                        if (elseProps.teamManager?.dateOfBirth) {
                            return dateUtils.convertDateToDate(
                                elseProps.teamManager.dateOfBirth,
                                INTERNATIONAL_DATE_FORMAT
                            );
                        }
                    }
                }

                return '--';
            }
        },
        {
            dataField: "fuzziness",
            text: t("Fuzziness"),
            sort: false,
            formatter: () => {
                return '80%'
            }
        }
    ];

    const handleFilterChange = useCallback((newFilters) => {
        setFilters((prev) => {
            return {
                ...prev,
                ...newFilters,
                pageIndex: 1
            }
        });
    }, []);

    const handleTableChange = (eventType, options) => {
        const {
            page,
            sizePerPage,

            sortField,
            sortOrder
        } = options;

        switch (eventType) {
            case 'pagination':
                setFilters({
                    ...filters,
                    pageIndex: page,
                    pageSize: sizePerPage
                });
                break;
            case 'sort':
                setFilters({
                    ...filters,
                    sortField: sortField,
                    sortOrder: sortOrder.toUpperCase()
                })
                break;
            default:
                return false;
        }
    }

    const NoDataIndication = () => (
        (itemsFetched && !(resultData?.items || []).length) ? <div className="alert m-0" role="alert">
            <p style={{
                textAlign: "center",
                marginBottom: 0
            }}>
                {t(EMPTY_LIST)}
            </p>
        </div> : <></>
    );

    const navigatorOptions = useMemo(() => {
        const complianceLabel = type.value === 'screening'
            ? "Compliance Screening"
            : "Compliance Monitoring";
        const queryName = queryDetailQuery.data?.query?.name || '...';

        return {
            translate: false,
            backButtonOptions: () => {
                return {
                    enable: true,
                    title: t("Back"),
                    icon: <i className="ri-arrow-left-fill"></i>,
                    onClick: () => history.goBack() 
                }
            },
            breadCrumbs: [
                {
                    title: t("SIP"),
                    link: "/admin/3rd-party/dashboard"
                },
                {
                    title: complianceLabel,
                    link: `/admin/3rd-party/compliance/${type.value}?tab=dashboard`
                },
                {
                    title: t('Queries'),
                    link: `/admin/3rd-party/compliance/${type.value}?tab=queries`
                },
                {
                    title: queryName,
                    link: '#'
                }
            ],
            pageTitle: queryName
        }
    }, [ 
        t, 
        history,
        type.value,
        queryDetailQuery.data
    ]);

    const totalItemsCount = parseInt(resultData?.itemsCount || 0);

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator {...navigatorOptions} />
                    </Col>

                    <Col lg="12" className="mb-2">
                        <DetailHeader 
                            t={t}
                            type={type}
                            id={id}
                            queryData={queryDetailQuery.data?.query}
                            isFetching={queryDetailQuery.isFetching}
                        />
                    </Col>

                    <Col lg="12">
                        <Card>
                            <CardHeader className="bg-white">
                                <h5 className="mb-1">
                                    {t('Search Items')}
                                </h5>
                                <p className="text-muted mb-0">
                                    {t(type.value === 'screening' ? 'The entities included in this query for screening.' : 'The entities included in this query for monitoring.')}
                                </p>
                            </CardHeader>

                            <CardBody>
                                <Row>
                                    <Col sm="12">
                                        <div
                                            style={{
                                                position: 'sticky',
                                                top: '74px',
                                                zIndex: 10
                                            }}
                                            className="d-flex justify-content-end align-items-center mb-3"
                                        >
                                            <div className="d-flex justify-content-end align-items-center bg-white">
                                                <div className="d-flex">
                                                    <FilteringComponent
                                                        onChange={handleFilterChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col sm="12">
                                        <PaginationProvider
                                            pagination={
                                                paginationFactory({
                                                    custom: true,
                                                    page: filters.pageIndex,
                                                    sizePerPage: filters.pageSize,
                                                    totalSize: totalItemsCount,
                                                    withFirstAndLast: true,
                                                    alwaysShowAllBtns: true,
                                                    prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                                    nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                                    prePageTitle: t('Pre page'),
                                                    firstPageTitle: t('First page'),
                                                    lastPageTitle: t('Last page'),
                                                    showTotal: false,
                                                    paginationTotalRenderer: (from, to, size) => {
                                                        return (
                                                            <span className="react-bootstrap-table-pagination-total">
                                                                {`${t('Total Items')} ${size}`}
                                                            </span>
                                                        )
                                                    },
                                                    hideSizePerPage: false,
                                                    sizePerPageList: [
                                                        {
                                                            text: '25',
                                                            value: 25
                                                        },
                                                        {
                                                            text: '50',
                                                            value: 50
                                                        }
                                                    ]
                                                })
                                            }>
                                            {
                                                ({
                                                    paginationProps,
                                                    paginationTableProps
                                                }) => (
                                                    <>
                                                        <Row>
                                                            <Col sm="12" className="mt-4">
                                                                <div>
                                                                    <div className="table-responsive">
                                                                        <BootstrapTable
                                                                            remote={{
                                                                                pagination: true,
                                                                                filter: false,
                                                                                sort: true,
                                                                                cellEdit: false,
                                                                                search: false
                                                                            }}
                                                                            loading={itemsFetching}
                                                                            overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                                                            onTableChange={handleTableChange}
                                                                            defaultSorted={[]}
                                                                            keyField="id"
                                                                            responsive={false}
                                                                            bordered={false}
                                                                            data={resultData?.items || []}
                                                                            striped={false}
                                                                            columns={tableColumns}
                                                                            classes={"table tpdd-table compliance-monitoring-table"}
                                                                            headerWrapperClasses={"thead-light"}
                                                                            style={{
                                                                                overflowX: "auto",
                                                                            }}
                                                                            noDataIndication={() => <NoDataIndication />}
                                                                            {...paginationTableProps}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>

                                                        <Row>
                                                            <Col sm="12">
                                                                <div className="tpdd-pagination-style-1">
                                                                    <PaginationTotalStandalone
                                                                        {...paginationProps}
                                                                    />

                                                                    {totalItemsCount > filters.pageSize && (
                                                                        <div>
                                                                            <PaginationListStandalone
                                                                                {...paginationProps}
                                                                            />

                                                                            <SizePerPageDropdownStandalone
                                                                                {...paginationProps}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )
                                            }

                                        </PaginationProvider>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

const ComplianceQueryDetails = ({
    t
}) => {
    const location = useLocation();
    const params = useParams();

    const searchQueries = new URLSearchParams(location.search);

    const type = searchQueries.get('type');

    const typeOption = ComplianceQueryTypes.find((option) => option.value === type)

    if (!typeOption) {
        return null
    }

    return (
        <ComplianceQueryDetailsBody 
            t={t}
            type={typeOption}
            id={params.queryId}
        />
    )
};

export default withNamespaces()(
    ComplianceQueryDetails
);